/**
 * Tap input: in the tutorial exercises played with "any key", a player the
 * app cannot hear (no MIDI keyboard) plays by tapping the exercise area, or by
 * pressing the space bar or any other computer key. Each press is one key of a
 * piano: press = note on, release = note off, always the same note. The note
 * goes through the same note path a MIDI key takes, so the exercise grades it
 * as it grades a keyboard player.
 *
 * Only these exercises take taps, and only when the input is not a MIDI
 * keyboard. Never counted: a press on a control (Next, Keep practicing, menus,
 * dialogs - anything outside the exercise area), a key with a modifier held,
 * the operating system's key repeat, a key typed into a text field.
 */

/** The exercise scenes that take taps: rhythm with any key, and Tap the beat. */
export const TAP_INPUT_SCENES: readonly string[] = [
  'NoteSequenceExercise',
  'ExerciseTempoPractice',
];

/** Whether the exercise scene named `sceneName` takes taps (when the app cannot hear the player). */
export const sceneTakesTaps = (sceneName: string | undefined): boolean =>
  sceneName !== undefined && TAP_INPUT_SCENES.includes(sceneName);

/** The note a tap plays (middle C). The exercises that take taps accept any note. */
export const TAP_NOTE = 60;

/** The velocity a tap plays at. */
export const TAP_VELOCITY = 100;

/**
 * On a touch screen a tap may land this much later after a note's onset than
 * a MIDI key and still count as on time (and as mistimed, at the far edge):
 * a touch reaches the app later than a key does. A first guess, not measured
 * on devices (to be tuned there); early edges are unchanged.
 */
export const TOUCH_TAP_LATE_ALLOWANCE_MS = 40;

/** The line under the instruction, telling the player how to play. */
export const tapInputHint = (touchDevice: boolean): string =>
  touchDevice ? 'Tap the screen to play this one.' : 'Press the space bar to play this one.';

/** Keys that never play: modifiers on their own, and keys that move focus, leave or call the system. */
const KEYS_THAT_NEVER_PLAY = new Set([
  'Shift',
  'Control',
  'Alt',
  'AltGraph',
  'Meta',
  'OS',
  'Fn',
  'FnLock',
  'Hyper',
  'Super',
  'CapsLock',
  'NumLock',
  'ScrollLock',
  'Tab',
  'Escape',
  'ContextMenu',
  'PrintScreen',
  'Dead',
  'Process',
  'Unidentified',
]);

const FUNCTION_KEY = /^F\d{1,2}$/;

export interface TapKeyEvent {
  key: string;
  repeat: boolean;
  shiftKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  metaKey: boolean;
  isComposing?: boolean;
  target: EventTarget | null;
}

interface ElementLike {
  tagName?: string;
  isContentEditable?: boolean;
  closest?: (selector: string) => unknown;
  matches?: (selector: string) => boolean;
}

const tagOf = (target: EventTarget | null): string =>
  ((target as ElementLike | null)?.tagName ?? '').toUpperCase();

const closestOf = (target: EventTarget | null, selector: string): boolean => {
  const element = target as ElementLike | null;
  return typeof element?.closest === 'function' && element.closest(selector) !== null;
};

/** A text field, where a key types rather than plays. */
export const isTextField = (target: EventTarget | null): boolean =>
  (target as ElementLike | null)?.isContentEditable === true ||
  ['INPUT', 'TEXTAREA', 'SELECT'].includes(tagOf(target)) ||
  closestOf(target, '[contenteditable]:not([contenteditable="false"])');

/** Focusable controls, where Space and Enter press (or type into) the control. */
const CONTROL_SELECTOR = [
  'button',
  'a[href]',
  'input',
  'select',
  'textarea',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="button"]',
  '[role="slider"]',
  '[role="menuitem"]',
].join(', ');

/** Open menus, popovers and dialogs: no key in them plays. */
const LAYER_SELECTOR = '[role="menu"], [role="dialog"], .MuiPopover-root, .MuiModal-root';

/**
 * Whether the focused element was focused from the keyboard (`:focus-visible`).
 * A button clicked with a mouse keeps focus in Chrome without it; Space or
 * Enter then belongs to the exercise, not to that button. Where the selector
 * cannot be read, the focus is taken as the keyboard's.
 *
 * Caution: focus set from script (`element.focus()`) reads as keyboard focus
 * in Chrome, even right after mouse play. Anything that puts focus back on a
 * control by script (a dialog restoring focus when it closes, say) makes Space
 * press that control again instead of playing; while taps play, the tutorial
 * hands focus back to the page after a control is used (see
 * useReturnFocusAfterControlUse), and new code should do the same.
 */
export const focusedFromKeyboard = (target: EventTarget | null): boolean => {
  const element = target as ElementLike | null;
  if (typeof element?.matches !== 'function') return true;
  try {
    return element.matches(':focus-visible');
  } catch {
    return true;
  }
};

/**
 * The key goes to a focused control: Space or Enter on a control focused from
 * the keyboard, or any key in an open menu or popover.
 */
const goesToAControl = (event: TapKeyEvent): boolean =>
  closestOf(event.target, LAYER_SELECTOR) ||
  ((event.key === ' ' || event.key === 'Enter') &&
    closestOf(event.target, CONTROL_SELECTOR) &&
    focusedFromKeyboard(event.target));

/** Whether a keydown (or the keyup of one) plays a tap. */
export const isTapKey = (event: TapKeyEvent): boolean =>
  !event.repeat &&
  !event.isComposing &&
  !event.shiftKey &&
  !event.ctrlKey &&
  !event.altKey &&
  !event.metaKey &&
  !KEYS_THAT_NEVER_PLAY.has(event.key) &&
  !FUNCTION_KEY.test(event.key) &&
  !isTextField(event.target) &&
  !goesToAControl(event);

/**
 * The presses held now, one tap note for them all. A new press while another
 * is held plays the note again (note off, then note on), as a second finger on
 * the same piano key would; the note ends when the press that played it last
 * is released.
 */
export interface TapPresses {
  /** The press that sounds the note now, if any. */
  sounding: string | null;
}

export type TapChange = 'noteOn' | 'retrigger' | 'noteOff' | 'none';

export const NO_PRESSES: TapPresses = { sounding: null };

/** A press `pressId` starts: what the note does, and the presses after it. */
export const pressStarts = (presses: TapPresses, pressId: string): [TapChange, TapPresses] => {
  if (presses.sounding === pressId) return ['none', presses];
  return [presses.sounding === null ? 'noteOn' : 'retrigger', { sounding: pressId }];
};

/** A press `pressId` ends: what the note does, and the presses after it. */
export const pressEnds = (presses: TapPresses, pressId: string): [TapChange, TapPresses] =>
  presses.sounding === pressId ? ['noteOff', NO_PRESSES] : ['none', presses];
